import React, { useEffect, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { AlertCircleIcon as AlertCircle, Download01Icon as Download, File01Icon as File, File01Icon as FileText, Folder01Icon as Folder, Image02Icon as Image, MusicNote01Icon as Music, Share01Icon as Share2, Video01Icon as Video } from '@hugeicons/core-free-icons';
import { Loader2 } from "@/components/ui/loader2";
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface SharedItem {
    id: string;
    name: string;
    type: 'file' | 'folder';
    size?: number;
    mime_type?: string;
    created_at: string;
    downloadUrl?: string;
    isPublic: boolean;
    allowDownload: boolean;
}

const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.genofx.com';

function getFileIcon(mimeType?: string, type?: string) {
    if (type === 'folder') return <HugeiconsIcon icon={Folder} className="h-16 w-16 text-blue-500"  />;
    if (!mimeType) return <HugeiconsIcon icon={File} className="h-16 w-16 text-gray-500"  />;

    if (mimeType.startsWith('image/')) return <HugeiconsIcon icon={Image} className="h-16 w-16 text-green-500"  />;
    if (mimeType.startsWith('video/')) return <HugeiconsIcon icon={Video} className="h-16 w-16 text-purple-500"  />;
    if (mimeType.startsWith('audio/')) return <HugeiconsIcon icon={Music} className="h-16 w-16 text-pink-500"  />;
    if (mimeType.includes('pdf')) return <HugeiconsIcon icon={FileText} className="h-16 w-16 text-red-500"  />;

    return <HugeiconsIcon icon={File} className="h-16 w-16 text-gray-500"  />;
}

function formatFileSize(bytes?: number): string {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export default function PublicSharePage() {
    const { shareId } = useParams<{ shareId: string }>();
    const [item, setItem] = useState<SharedItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!shareId) {
            setError('Invalid share link');
            setLoading(false);
            return;
        }

        fetchSharedItem();
    }, [shareId]);

    const fetchSharedItem = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/drive/public/${shareId}`);
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Item not found or not public');
            }

            setItem(data.item);
        } catch (err: any) {
            setError(err.message || 'Failed to load shared item');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!item?.downloadUrl) return;

        setDownloading(true);
        try {
            // Open download URL in new tab
            window.open(item.downloadUrl, '_blank');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                    <p className="text-slate-400">Loading shared file...</p>
                </div>
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
                    <CardContent className="pt-6 text-center">
                        <HugeiconsIcon icon={AlertCircle} className="h-16 w-16 text-red-500 mx-auto mb-4"  />
                        <h2 className="text-xl font-semibold text-white mb-2">File Not Available</h2>
                        <p className="text-slate-400 mb-6">
                            {error || 'This shared file is no longer available or the link is invalid.'}
                        </p>
                        <p className="text-slate-500 text-sm">
                            The owner may have removed the file or disabled sharing.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
            <Card className="max-w-lg w-full bg-slate-800/50 border-slate-700 backdrop-blur-sm">
                <CardHeader className="text-center pb-2">
                    <div className="flex justify-center mb-4">
                        <div className="p-4 bg-slate-700/50 rounded-2xl">
                            {getFileIcon(item.mime_type, item.type)}
                        </div>
                    </div>
                    <h1 className="text-xl font-semibold text-white truncate">{item.name}</h1>
                    <p className="text-slate-400 text-sm">
                        {item.type === 'folder' ? 'Shared Folder' : formatFileSize(item.size)}
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* File Info */}
                    <div className="bg-slate-700/30 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Type</span>
                            <span className="text-white">{item.mime_type || 'Folder'}</span>
                        </div>
                        {item.size && (
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Size</span>
                                <span className="text-white">{formatFileSize(item.size)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Shared</span>
                            <span className="text-white">
                                {new Date(item.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>

                    {/* Actions */}
                    {item.type === 'file' && item.allowDownload && (
                        <Button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                            size="lg"
                        >
                            {downloading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <HugeiconsIcon icon={Download} className="h-5 w-5"  />
                            )}
                            Download File
                        </Button>
                    )}

                    {!item.allowDownload && (
                        <p className="text-center text-slate-400 text-sm">
                            Downloads are disabled for this file.
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Footer */}
            <div className="fixed bottom-4 text-center w-full">
                <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
                    <HugeiconsIcon icon={Share2} className="h-4 w-4"  />
                    <span>Shared via Genofx Drive</span>
                </div>
            </div>
        </div>
    );
}

